<script setup>
import { ref, onMounted, computed } from 'vue';
import { useToast } from '@/composables/useToast.js';

const props = defineProps({
    gateway: {
        type: Object,
        required: true
    }
});

const { success, error, warning } = useToast();
const loading = ref(false);
const fundingStatus = ref({
    has_funding_wallet: false,
    funding_wallet_address: null,
    is_encrypted: false,
    trx_balance: null
});

const showFundingWalletModal = ref(false);
const showSyncConfirmModal = ref(false);
const privateKey = ref('');
const confirmPrivateKey = ref('');
const showKey = ref(false);

const isTronGateway = computed(() => {
    return ['tron', 'tron-network', 'tron-usdt'].includes(props.gateway.slug);
});

const balanceStatus = computed(() => {
    if (!fundingStatus.value.trx_balance) return 'unknown';
    if (fundingStatus.value.trx_balance < 10) return 'low';
    if (fundingStatus.value.trx_balance < 50) return 'medium';
    return 'good';
});

const balanceColor = computed(() => {
    switch (balanceStatus.value) {
        case 'low': return 'text-red-600 dark:text-red-400';
        case 'medium': return 'text-amber-600 dark:text-amber-400';
        case 'good': return 'text-green-600 dark:text-green-400';
        default: return 'text-gray-600 dark:text-gray-400';
    }
});

onMounted(() => {
    if (isTronGateway.value) {
        loadFundingStatus();
    }
});


const loadFundingStatus = async () => {
    try {
        loading.value = true;
        const response = await fetch(`/admin/payment-gateways/${props.gateway.id}/tron/funding-wallet-status`);
        const data = await response.json();
        
        if (data.success) {
            fundingStatus.value = {
                has_funding_wallet: data.has_funding_wallet,
                funding_wallet_address: data.funding_wallet_address,
                is_encrypted: data.is_encrypted,
                trx_balance: data.trx_balance
            };
        }
    } catch (err) {
        console.error('Failed to load funding wallet status:', err);
    } finally {
        loading.value = false;
    }
};

const saveFundingWallet = async () => {
    if (!privateKey.value) {
        error('Please enter the private key');
        return;
    }
    
    if (!privateKey.value.match(/^[a-fA-F0-9]{64}$/)) {
        error('Private key must be 64 hexadecimal characters (0-9, a-f)');
        return;
    }
    
    if (privateKey.value !== confirmPrivateKey.value) {
        error('Private keys do not match');
        return;
    }

    try {
        loading.value = true;
        const response = await fetch(`/admin/payment-gateways/${props.gateway.id}/tron/update-funding-wallet`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content
            },
            body: JSON.stringify({
                private_key: privateKey.value
            })
        });
        
        const data = await response.json();
        
        if (data.success) {
            success('✅ Funding wallet configured successfully!');
            success(`📍 Address: ${data.funding_wallet_address}`);
            
            showFundingWalletModal.value = false;
            privateKey.value = '';
            confirmPrivateKey.value = '';
            await loadFundingStatus();
            
            // Show reminder to fund the wallet
            setTimeout(() => {
                warning('⚠️ Remember to send TRX to the funding wallet for auto-funding to work!');
            }, 2000);
        } else {
            error(data.message || 'Failed to configure funding wallet');
        }
    } catch (err) {
        error('Failed to configure funding wallet. Please try again.');
        console.error(err);
    } finally {
        loading.value = false;
    }
};

const copyAddressToClipboard = () => {
    if (!fundingStatus.value.funding_wallet_address) return;
    
    navigator.clipboard.writeText(fundingStatus.value.funding_wallet_address).then(() => {
        success('Address copied to clipboard');
    }).catch(err => {
        error('Failed to copy to clipboard');
        console.error(err);
    });
};

const openModal = () => {
    showFundingWalletModal.value = true;
    privateKey.value = '';
    confirmPrivateKey.value = '';
    showKey.value = false;
};

const closeModal = () => {
    showFundingWalletModal.value = false;
    privateKey.value = '';
    confirmPrivateKey.value = '';
    showKey.value = false;
};

const refreshBalance = async () => {
    await loadFundingStatus();
    success('Balance refreshed');
};

const openSyncConfirmModal = () => {
    showSyncConfirmModal.value = true;
};

const closeSyncConfirmModal = () => {
    showSyncConfirmModal.value = false;
};

const confirmSyncWithMasterSeed = async () => {
    showSyncConfirmModal.value = false;
    
    try {
        loading.value = true;
        const response = await fetch(`/admin/payment-gateways/${props.gateway.id}/tron/sync-funding-wallet`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content
            }
        });
        
        const data = await response.json();
        
        if (data.success) {
            success('✅ Funding wallet synced with master seed!');
            success(`📍 New address: ${data.funding_wallet_address}`);
            await loadFundingStatus();
            
            setTimeout(() => {
                warning('⚠️ Remember to send TRX to the new funding wallet address!');
            }, 2000);
        } else {
            error(data.message || 'Failed to sync funding wallet');
        }
    } catch (err) {
        error('Failed to sync funding wallet');
        console.error(err);
    } finally {
        loading.value = false;
    }
};
</script>

<template>
  <div
    v-if="isTronGateway"
    class="bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 rounded-xl shadow-sm border-2 border-emerald-200 dark:border-emerald-700 p-6"
  >
    <div class="flex items-start justify-between mb-4">
      <div class="flex items-start space-x-3">
        <div class="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
          <svg
            class="w-6 h-6 text-emerald-600 dark:text-emerald-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"
            />
          </svg>
        </div>
        <div>
          <h3 class="text-lg font-semibold text-emerald-900 dark:text-emerald-100">
            Auto-Funding Wallet
          </h3>
          <p class="text-sm text-emerald-700 dark:text-emerald-300 mt-1">
            Configure a separate wallet to automatically fund user wallets with TRX for gas fees
          </p>
        </div>
      </div>
      <button
        type="button"
        @click="openModal"
        class="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors text-sm font-medium flex items-center space-x-2"
        :disabled="loading"
      >
        <svg
          class="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
          />
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
          />
        </svg>
        <span>{{ fundingStatus.has_funding_wallet ? 'Update' : 'Configure' }} Funding Wallet</span>
      </button>
    </div>

    <div v-if="loading && !fundingStatus.has_funding_wallet" class="text-center py-8">
      <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
      <p class="mt-2 text-sm text-gray-600 dark:text-gray-400">Loading funding wallet status...</p>
    </div>

    <div v-else-if="!fundingStatus.has_funding_wallet" class="text-center py-8">
      <div class="mb-4">
        <svg
          class="w-16 h-16 mx-auto text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      </div>
      <h4 class="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
        Funding Wallet Not Configured
      </h4>
      <p class="text-sm text-gray-600 dark:text-gray-400 mb-4 max-w-md mx-auto">
        Configure a funding wallet to enable automatic TRX funding for user wallets.
        This allows seamless USDT sweeps without manual intervention.
      </p>
      <button
        type="button"
        @click="openModal"
        class="px-6 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors text-sm font-medium"
      >
        Configure Now
      </button>
    </div>

    <div v-else class="space-y-4">
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <!-- Funding Wallet Address -->
        <div class="bg-white dark:bg-gray-800 rounded-lg p-4 border border-emerald-200 dark:border-emerald-700">
          <div class="flex items-center justify-between mb-2">
            <h4 class="text-sm font-medium text-gray-700 dark:text-gray-300">Funding Wallet Address</h4>
            <button
              type="button"
              @click="copyAddressToClipboard"
              class="p-1 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 rounded transition-colors"
              title="Copy address"
            >
              <svg
                class="w-4 h-4 text-emerald-600 dark:text-emerald-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                />
              </svg>
            </button>
          </div>
          <p class="text-xs font-mono text-gray-900 dark:text-gray-100 break-all">
            {{ fundingStatus.funding_wallet_address }}
          </p>
          <a
            :href="`https://nile.tronscan.org/#/address/${fundingStatus.funding_wallet_address}`"
            target="_blank"
            class="text-xs text-emerald-600 dark:text-emerald-400 hover:underline mt-2 inline-block"
          >
            View on TronScan →
          </a>
        </div>

        <!-- TRX Balance -->
        <div class="bg-white dark:bg-gray-800 rounded-lg p-4 border border-emerald-200 dark:border-emerald-700">
          <div class="flex items-center justify-between mb-2">
            <h4 class="text-sm font-medium text-gray-700 dark:text-gray-300">TRX Balance</h4>
            <button
              type="button"
              @click="refreshBalance"
              :disabled="loading"
              class="p-1 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 rounded transition-colors"
              title="Refresh balance"
            >
              <svg
                class="w-4 h-4 text-emerald-600 dark:text-emerald-400"
                :class="{ 'animate-spin': loading }"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            </button>
          </div>
          <p class="text-2xl font-bold" :class="balanceColor">
            {{ fundingStatus.trx_balance !== null ? fundingStatus.trx_balance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '...' }} TRX
          </p>
          <p v-if="balanceStatus === 'low'" class="text-xs text-red-600 dark:text-red-400 mt-1">
            ⚠️ Low balance! Send more TRX for auto-funding.
          </p>
          <p v-else-if="balanceStatus === 'medium'" class="text-xs text-amber-600 dark:text-amber-400 mt-1">
            ⚡ Balance is adequate. Consider adding more TRX soon.
          </p>
          <p v-else-if="balanceStatus === 'good'" class="text-xs text-green-600 dark:text-green-400 mt-1">
            ✅ Sufficient balance for auto-funding.
          </p>
        </div>
      </div>

      <!-- Security Status -->
      <div class="bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-4 border border-emerald-200 dark:border-emerald-700">
        <div class="flex items-start space-x-3">
          <svg
            class="w-5 h-5 text-emerald-600 dark:text-emerald-400 mt-0.5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
            />
          </svg>
          <div class="flex-1">
            <h4 class="text-sm font-medium text-emerald-900 dark:text-emerald-100 mb-1">
              Security Status
            </h4>
            <div class="space-y-1 text-xs text-emerald-700 dark:text-emerald-300">
              <div class="flex items-center space-x-2">
                <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                </svg>
                <span>Private key encrypted with Laravel Crypt</span>
              </div>
              <div class="flex items-center space-x-2">
                <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                </svg>
                <span>Separate from main wallet (USDT receiver)</span>
              </div>
              <div class="flex items-center space-x-2">
                <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                </svg>
                <span>Only sends TRX for gas fees (2 TRX per funding)</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Master Seed Sync Options -->
      <div class="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4 border border-purple-200 dark:border-purple-700">
        <div class="flex items-start space-x-3">
          <svg
            class="w-5 h-5 text-purple-600 dark:text-purple-400 mt-0.5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
          <div class="flex-1">
            <h4 class="text-sm font-medium text-purple-900 dark:text-purple-100 mb-2">
              Master Seed Integration
            </h4>
            <p class="text-xs text-purple-700 dark:text-purple-300 mb-3">
              When you change your master seed, click the button below to automatically sync the funding wallet with the new private key.
            </p>
            <button
              type="button"
              @click="openSyncConfirmModal"
              :disabled="loading"
              class="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <span>Sync with Master Seed</span>
            </button>
          </div>
        </div>
      </div>

      <!-- How It Works -->
      <div class="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-700">
        <h4 class="text-sm font-medium text-blue-900 dark:text-blue-100 mb-2 flex items-center space-x-2">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>How Auto-Funding Works</span>
        </h4>
        <ol class="space-y-1 text-xs text-blue-700 dark:text-blue-300">
          <li class="flex items-start space-x-2">
            <span class="font-semibold min-w-[1.5rem]">1.</span>
            <span>User deposits USDT to their wallet</span>
          </li>
          <li class="flex items-start space-x-2">
            <span class="font-semibold min-w-[1.5rem]">2.</span>
            <span>System detects deposit and checks user's TRX balance</span>
          </li>
          <li class="flex items-start space-x-2">
            <span class="font-semibold min-w-[1.5rem]">3.</span>
            <span>If TRX < 2, funding wallet automatically sends 2 TRX to user</span>
          </li>
          <li class="flex items-start space-x-2">
            <span class="font-semibold min-w-[1.5rem]">4.</span>
            <span>System sweeps USDT to main wallet</span>
          </li>
          <li class="flex items-start space-x-2">
            <span class="font-semibold min-w-[1.5rem]">5.</span>
            <span>User is credited with balance (minus sweep fee)</span>
          </li>
        </ol>
      </div>
    </div>

    <!-- Configuration Modal -->
    <div
      v-if="showFundingWalletModal"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      @click.self="closeModal"
    >
      <div class="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div class="p-6 border-b border-gray-200 dark:border-gray-700">
          <div class="flex items-center justify-between">
            <h3 class="text-xl font-semibold text-gray-900 dark:text-gray-100">
              Configure Funding Wallet
            </h3>
            <button
              type="button"
              @click="closeModal"
              class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div class="p-6 space-y-4">
          <!-- Warning -->
          <div class="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-4">
            <div class="flex items-start space-x-3">
              <svg class="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div class="flex-1">
                <h4 class="text-sm font-medium text-amber-900 dark:text-amber-100 mb-1">Important Security Information</h4>
                <ul class="text-xs text-amber-700 dark:text-amber-300 space-y-1">
                  <li>• This wallet will ONLY send TRX for gas fees</li>
                  <li>• It should be SEPARATE from your main USDT receiving wallet</li>
                  <li>• Private key will be encrypted and stored securely</li>
                  <li>• Make sure you have the correct private key before saving</li>
                </ul>
              </div>
            </div>
          </div>

          <!-- Private Key Input -->
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Private Key (64 hexadecimal characters)
            </label>
            <div class="relative">
              <input
                v-model="privateKey"
                :type="showKey ? 'text' : 'password'"
                placeholder="Enter private key (0-9, a-f)"
                class="w-full px-4 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-emerald-500 dark:bg-gray-700 dark:text-gray-100 font-mono text-sm"
                maxlength="64"
              />
              <button
                type="button"
                @click="showKey = !showKey"
                class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <svg v-if="!showKey" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                </svg>
              </button>
            </div>
            <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Length: {{ privateKey.length }}/64 characters
            </p>
          </div>

          <!-- Confirm Private Key -->
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Confirm Private Key
            </label>
            <input
              v-model="confirmPrivateKey"
              type="password"
              placeholder="Re-enter private key to confirm"
              class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-emerald-500 dark:bg-gray-700 dark:text-gray-100 font-mono text-sm"
              maxlength="64"
            />
          </div>

          <!-- Info -->
          <div class="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
            <h4 class="text-sm font-medium text-blue-900 dark:text-blue-100 mb-2">What happens next?</h4>
            <ol class="text-xs text-blue-700 dark:text-blue-300 space-y-1 list-decimal list-inside">
              <li>Private key will be encrypted using Laravel's encryption</li>
              <li>TRON address will be generated from the private key</li>
              <li>Both will be saved to the gateway credentials</li>
              <li>You'll need to send TRX to the funding wallet address</li>
              <li>Auto-funding will activate automatically</li>
            </ol>
          </div>
        </div>

        <div class="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
          <button
            type="button"
            @click="closeModal"
            class="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            :disabled="loading"
          >
            Cancel
          </button>
          <button
            type="button"
            @click="saveFundingWallet"
            class="px-6 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors flex items-center space-x-2"
            :disabled="loading || !privateKey || privateKey !== confirmPrivateKey"
          >
            <svg v-if="loading" class="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            <span>{{ loading ? 'Saving...' : 'Save & Encrypt' }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Sync Confirmation Modal -->
  <div
    v-if="showSyncConfirmModal"
    class="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
    @click.self="closeSyncConfirmModal"
  >
    <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full overflow-hidden transform transition-all">
      <div class="bg-gradient-to-r from-purple-600 to-indigo-600 p-6">
        <div class="flex items-center space-x-4">
          <div class="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
            <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <div>
            <h3 class="text-xl font-bold text-white">Sync Funding Wallet</h3>
            <p class="text-purple-100 text-sm">Master Seed Integration</p>
          </div>
        </div>
      </div>

      <div class="p-6 space-y-4">
        <div class="bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-500 p-4 rounded">
          <div class="flex items-start space-x-3">
            <svg class="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div class="flex-1">
              <h4 class="text-sm font-semibold text-amber-800 dark:text-amber-200 mb-1">Important Warning</h4>
              <p class="text-sm text-amber-700 dark:text-amber-300">
                This action will generate a new funding wallet address based on your current master seed.
              </p>
            </div>
          </div>
        </div>

        <div class="space-y-3">
          <h4 class="text-sm font-semibold text-gray-900 dark:text-gray-100">What will happen:</h4>
          <ul class="space-y-2 text-sm text-gray-700 dark:text-gray-300">
            <li class="flex items-start space-x-2">
              <svg class="w-5 h-5 text-purple-600 dark:text-purple-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>A new funding wallet will be derived from your master seed (index 20)</span>
            </li>
            <li class="flex items-start space-x-2">
              <svg class="w-5 h-5 text-purple-600 dark:text-purple-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>The new wallet address will be different from the current one</span>
            </li>
            <li class="flex items-start space-x-2">
              <svg class="w-5 h-5 text-purple-600 dark:text-purple-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Private key will be encrypted and stored securely</span>
            </li>
            <li class="flex items-start space-x-2">
              <svg class="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <span><strong>You must send TRX to the new address</strong> for auto-funding to work</span>
            </li>
          </ul>
        </div>

        <div class="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-3">
          <p class="text-xs text-blue-800 dark:text-blue-200">
            <strong>Tip:</strong> After syncing, transfer any remaining TRX from the old funding wallet to the new one to avoid losing funds.
          </p>
        </div>
      </div>

      <div class="p-6 bg-gray-50 dark:bg-gray-900/50 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between space-x-3">
        <button
          type="button"
          @click="closeSyncConfirmModal"
          class="flex-1 px-4 py-2.5 border-2 border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
        >
          Cancel
        </button>
        <button
          type="button"
          @click="confirmSyncWithMasterSeed"
          class="flex-1 px-4 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-medium rounded-lg transition-all shadow-lg hover:shadow-xl flex items-center justify-center space-x-2"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          <span>Sync Wallet</span>
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Add any additional styles if needed */
</style>
